import { MobxManager } from 'framework/mobx-integration';
import { DataRegistryEntry } from 'areas/management/data-registry/dataRegistryEntry';
import { makeAutoObservable } from 'mobx';
import { apiFetch } from 'framework/api';
import { getDataRegistry } from 'areas/management/data-registry/api';
import { GridSelectionMode } from 'controls/grid/gridSelection';
import { getMetricUnits, validateConversion } from 'areas/service-boards/widgets/common/dataSources/MetricSettingsApi';
import { formatNumber } from 'tools/helpers/math';

import { MetricConfigurationUnitType } from 'areas/metrics/components/MetricSelector/types';
import { MetricSelectorProps } from 'areas/metrics/components/MetricSelector/props';
import { i18n } from 'core/localization';
import { AntDefaultOptionType } from 'controls/react/ant/ant-select/types';

export class MetricSelectorStore {
	mobx = new MobxManager();
	units: AntDefaultOptionType[];
	dataRegistryEntry: DataRegistryEntry;
	loaded = false;
	loading = false;

	validating = false;
	validationResult: string;
	validationResultVisible = false;

	get advancedInitial() {
		return (
			(this.model?.unit != null && this.model?.unit != MetricConfigurationUnitType.Autoscaling) ||
			this.model?.unitLabel?.length > 0 ||
			this.model?.formula?.length > 0 ||
			this.model?.decimals != null
		);
	}

	constructor(public props: MetricSelectorProps) {
		makeAutoObservable(this);

		this.mobx.reaction(
			() => this.model?.unit,
			() => {
				if (!this.model?.unit) return;

				if (this.model.unit == MetricConfigurationUnitType.Autoscaling) {
					this.model.formula = null;
					this.model.unitLabel = null;
				}
			},
		);
	}

	async init() {
		this.mobx.reaction(() => this.model?.metricId, () => {
			this.model.unit = MetricConfigurationUnitType.Autoscaling
			this.model.unitLabel = ''
			void this.loadData()
		})

		this.mobx.reaction(x => this.model?.formula, (x) => {
			void this.validateFormula()
		})

		await this.loadData()
	}

	get model() {
		return this.props?.model;
	}

	async loadData() {
		this.loading = true;
		this.loaded = true;
		this.units = [];
		this.dataRegistryEntry = null;

		await this.loadMetric();
		await this.loadAvailableUnitsForMetric();

		this.loading = false;
	}

	async loadMetric() {
		if (this.model?.metricId == null) return;

		const response = await apiFetch(
			getDataRegistry({
				accountId: this.props.accountId,
				selection: {
					ids: [this.model.metricId],
					mode: GridSelectionMode.Include,
				},
			}),
		);

		if (!response.success || !response.data?.items?.length) {
			return;
		}

		this.dataRegistryEntry = response.data.items[0].data;
	}

	async loadAvailableUnitsForMetric() {
		if (!this.dataRegistryEntry) return;

		const unitType = this.dataRegistryEntry.unitType;
		const response = await apiFetch(getMetricUnits(unitType));

		if (response.success) {
			const units = response.data;
			const defaultIndex = units.findIndex((x) => x.value == this.dataRegistryEntry.unitType);
			if (defaultIndex >= 0) {
				units[defaultIndex].description = i18n('{0} (default)', units[defaultIndex].description);
			}

			this.units = [
				{
					label: i18n('Autoscaling'),
					value: MetricConfigurationUnitType.Autoscaling,
				},
				{
					label: i18n('Custom'),
					value: MetricConfigurationUnitType.Custom,
				},
				...units.map((x) => ({ label: x.description, value: x.value })),
			];

			this.loaded = true;
		}
	}

	validateFormula = async () => {
		if (this.model?.unit != MetricConfigurationUnitType.Custom) return

		this.validating = true
		const result = await apiFetch(validateConversion(this.model.formula))

		if (!result.success) {
			this.validationResult = i18n('Formula is not valid');
		} else if (result.data) {
			this.validationResult = i18n('Formula is valid. Output is: {0}.', formatNumber(result.data, this.model.decimals));
		} else {
			this.validationResult = i18n('Formula is valid');
		}

		this.validationResultVisible = true;
		setTimeout(() => (this.validationResultVisible = false), 3000);

		this.validating = false;
	};

	destroy() {
		this.mobx.destroy();
	}
}
